import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import CampaignIcon from '@mui/icons-material/Campaign';
import Close from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import {
  Announcement,
  AnnouncementReadSignal,
  AnnouncementSignal,
  SIGNALS_CHANNEL_ANNOUNCEMENTS,
  SIGNALS_CHANNEL_ANNOUNCEMENTS_READ,
} from '@internal/backstage-plugin-announcements-common';
import { announcementsApiRef } from '../../apis';
import { useAnnouncements, useAnnouncementsTranslation } from '../../hooks';
import { useAnnouncementsRoute } from '../../hooks/useAnnouncementsRoute';
import { announcementViewRouteRef } from '../../routes';

const useStyles = makeStyles((theme: Theme) => ({
  // showing on top, as a block
  blockPositioning: {
    position: 'relative',
    top: -15,
    zIndex: 1,
  },
  // showing on top, as a floating alert below the global header
  floatingPositioning: {
    '&.MuiSnackbar-anchorOriginTopCenter': {
      top: 80,
    },
  },
  icon: {
    fontSize: 20,
    color: '#030220',
  },
  bannerIcon: {
    fontSize: 25,
    marginRight: '0.5rem',
    color: '#030220',
    verticalAlign: 'middle',
  },
  commonStyle: {
    color: '#1d0638',
    textDecoration: 'none',
    fontWeight: 'bold',
  },
  excerptStyle: {
    color: '#1d0638',
    textDecoration: 'none',
    marginLeft: '8px',
  },
  content: {
    width: '100%',
    maxWidth: 'inherit',
    flexWrap: 'nowrap',
    // PO asked for this custom color until the banner is themed
    backgroundColor: '#fef04a',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.banner?.text,
    '& a': {
      color: theme.palette.banner?.link,
    },
  },
}));

type AnnouncementBannerProps = {
  announcement: Announcement;
  variant?: 'block' | 'floating';
};

const AnnouncementBanner = (props: AnnouncementBannerProps) => {
  const classes = useStyles();
  const announcementsApi = useApi(announcementsApiRef);
  const toastApi = useApi(toastApiRef);
  const viewAnnouncementLink = useAnnouncementsRoute(announcementViewRouteRef);
  const { t } = useAnnouncementsTranslation();
  const [bannerOpen, setBannerOpen] = useState(true);
  const variant = props.variant || 'block';
  const announcement = props.announcement;

  const handleClick = () => {
    const announcementCreatedAt = DateTime.fromISO(announcement.created_at);
    if (announcementCreatedAt > announcementsApi.lastSeenDate()) {
      announcementsApi.markLastSeenDate(announcementCreatedAt);
    }
    announcementsApi
      .markAnnouncementRead({ ids: [announcement.id], read: true })
      .catch(() => {
        toastApi.post({
          title: t('newAnnouncementBanner.markAsReadFailed'),
          status: 'danger',
        });
      });
    setBannerOpen(false);
  };

  const message = (
    <>
      <CampaignIcon className={classes.bannerIcon} />
      <Link
        to={viewAnnouncementLink({ id: announcement.id })}
        className={classes.commonStyle}
      >
        {announcement.title}
      </Link>

      <span className={classes.excerptStyle}>
        &nbsp;– {announcement.excerpt}
      </span>
    </>
  );

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={bannerOpen}
      className={
        variant === 'block'
          ? classes.blockPositioning
          : classes.floatingPositioning
      }
    >
      <SnackbarContent
        className={classes.content}
        message={message}
        action={[
          <IconButton
            key="dismiss"
            title={t('newAnnouncementBanner.markAsSeen')}
            color="inherit"
            onClick={handleClick}
          >
            <Close className={classes.icon} />
          </IconButton>,
        ]}
      />
    </Snackbar>
  );
};

type NewAnnouncementBannerProps = {
  variant?: 'block' | 'floating';
  max?: number;
  category?: string;
  active?: boolean;
};

/**
 * Shows the announcements the user has not seen yet, one banner each. A new
 * announcement published while the app is open is added through signals.
 */
export const NewAnnouncementBanner = (props: NewAnnouncementBannerProps) => {
  const { max, category, active, variant } = props;
  const announcementsApi = useApi(announcementsApiRef);
  const [signaledAnnouncements, setSignaledAnnouncements] = useState<
    Announcement[]
  >([]);
  const [readAnnouncementIds, setReadAnnouncementIds] = useState<Set<string>>(
    () => new Set(),
  );

  const { announcements, loading, error } = useAnnouncements({
    max: max ?? 1,
    category,
    active,
  });
  const { lastSignal } = useSignal<AnnouncementSignal>(
    SIGNALS_CHANNEL_ANNOUNCEMENTS,
  );
  const { lastSignal: readSignal } = useSignal<AnnouncementReadSignal>(
    SIGNALS_CHANNEL_ANNOUNCEMENTS_READ,
  );

  useEffect(() => {
    if (!lastSignal) {
      return;
    }
    setSignaledAnnouncements(current =>
      current.some(announcement => announcement.id === lastSignal.data.id)
        ? current
        : [...current, lastSignal.data],
    );
  }, [lastSignal]);

  useEffect(() => {
    if (!readSignal) {
      return;
    }
    setReadAnnouncementIds(current => {
      const updated = new Set(current);
      readSignal.announcement_ids.forEach(id => updated.add(id));
      return updated;
    });
  }, [readSignal]);

  if (loading) {
    return null;
  } else if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (announcements.count === 0 && signaledAnnouncements.length === 0) {
    return null;
  }

  const lastSeen = announcementsApi.lastSeenDate();
  const unseenAnnouncements = announcements.results.filter(announcement => {
    return (
      !announcement.isRead &&
      !readAnnouncementIds.has(announcement.id) &&
      lastSeen < DateTime.fromISO(announcement.created_at)
    );
  });

  const matchingSignals = signaledAnnouncements.filter(announcement => {
    return (
      !readAnnouncementIds.has(announcement.id) &&
      (active === undefined || announcement.active === active) &&
      (!category || announcement.category?.slug === category)
    );
  });
  for (const announcement of matchingSignals) {
    if (!unseenAnnouncements.some(item => item.id === announcement.id)) {
      unseenAnnouncements.push(announcement);
    }
  }

  if (unseenAnnouncements.length === 0) {
    return null;
  }

  return (
    <>
      {unseenAnnouncements.map(announcement => (
        <AnnouncementBanner
          key={announcement.id}
          announcement={announcement}
          variant={variant}
        />
      ))}
    </>
  );
};
